import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../types';
import { FormInputGroup } from '../../molecules';

export interface FormTextInputProps
    extends CommonProps,
        VariantProps<typeof FormTextInputStyles> {
    id: string;
    name?: string;
    type?: 'text' | 'email';
    label: string;
    isDisabled?: boolean;
    hasErrors?: boolean;
    required?: boolean;
    wrapperClassName?: string;
}

export const FormTextInputStyles = tv({
    slots: {
        input: 'block rounded-full bg-gray-500 peer py-2 px-5 peer w-full',
        label: 'absolute top-0 left-2 text-gray-100 text-sm peer-placeholder-shown:text-base peer-placeholder-shown:translate-x-3 peer-placeholder-shown:translate-y-9 transition',
    },
    variants: {
        labelHidden: {
            true: {
                label: 'sr-only',
                input: 'placeholder:text-gray-100',
            },
            false: {
                input: 'mt-7 placeholder-transparent',
            },
        },
        hasValue: {
            true: {
                label: '',
            },
        },
        hasErrors: {
            true: {
                input: 'border-danger-500 border',
                label: 'text-danger-500',
            },
            false: {},
        },
        required: {
            true: {
                label: "after:content-['*']",
            },
        },
    },
});

export const FormTextInput = ({
    className,
    wrapperClassName,
    id,
    name,
    type = 'text',
    testId = 'form-text-input',
    labelHidden,
    label,
    isDisabled,
    hasErrors = false,
    required,
}: FormTextInputProps): JSX.Element => {
    const { label: labelStyles, input } = FormTextInputStyles({
        labelHidden,
        hasErrors,
    });

    return (
        <FormInputGroup className={wrapperClassName}>
            <input
                data-testid={testId}
                className={input({ className })}
                id={id}
                name={name ?? id}
                placeholder={`${label}${required && labelHidden ? '*' : ''}`}
                type={type}
                {...(required && { required })}
                {...(isDisabled && { disabled: isDisabled })}
            />
            <label className={labelStyles({ required })} htmlFor={id}>
                {label}
            </label>
        </FormInputGroup>
    );
};
